import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Category as CategoryIcon,
  Search as SearchIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import {
  allModuleMenus,
  fashionMenuItem,
  COLORES_ICONOS_SUBMENU,
} from '../config/navigationMenu';
import { navigateWithAdminBack, clearAdminBackTo } from '../utils/adminNavigationContext';
import { matchesSharedModuleRoute, navigateWithModuleBack } from '../utils/moduleNavigationContext';
import { usePermissions } from '../contexts/PermissionContext';

export interface ModuloFuncionalidadItem {
  text: string;
  path: string;
  description?: string;
  icon?: React.ReactNode;
}

interface ModuloFuncionalidadesProps {
  moduloPath?: string;
  items?: ModuloFuncionalidadItem[];
  /** 1 = tamaño estándar; 0.75 = ~75 % altura/padding y grilla más densa; valores menores = más compacto. */
  cardScale?: number;
  showBackButton?: boolean;
  backPath?: string;
  backLabel?: string;
  /** Origen en Administración para "Volver al Menu" en pantallas hijas. */
  adminBackTo?: string;
}

type VistaMenu = 'lista' | 'tarjetas' | 'iconos';

const VISTA_STORAGE_KEY = 'moduloFuncionalidades.vista';

const CONTABILIDAD_ACCESOS_OCULTOS = new Set([
  '/contabilidad/imputables',
  '/contabilidad/noimputables',
]);

function isVistaMenu(value: string | null): value is VistaMenu {
  return value === 'lista' || value === 'tarjetas' || value === 'iconos';
}

function readVistaMenu(): VistaMenu {
  try {
    const stored = localStorage.getItem(VISTA_STORAGE_KEY);
    return isVistaMenu(stored) ? stored : 'tarjetas';
  } catch {
    return 'tarjetas';
  }
}

function normalizar(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const vistaButtonSx = (active: boolean) => ({
  color: active ? 'primary.main' : 'text.secondary',
  bgcolor: active ? 'primary.light' : 'transparent',
  '&:hover': { bgcolor: active ? 'primary.light' : 'action.hover' },
});

const cardSx = {
  borderRadius: 3,
  border: '1px solid #e5e7eb',
  boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
  height: '100%',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.12)',
  },
};

function CajaIcono({
  color,
  size,
  children,
}: {
  color: string;
  size: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        bgcolor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        flexShrink: 0,
        '& .MuiSvgIcon-root': { fontSize: Math.round(size * 0.5) },
      }}
    >
      {children ?? <CategoryIcon />}
    </Box>
  );
}

const ModuloFuncionalidades: React.FC<ModuloFuncionalidadesProps> = ({
  moduloPath,
  items: customItems,
  cardScale = 1,
  showBackButton = true,
  backPath = '/menu-principal',
  backLabel = 'Volver al Menu Principal',
  adminBackTo,
}) => {
  const navigate = useNavigate();
  const { canPage } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [vista, setVista] = useState<VistaMenu>(readVistaMenu);
  const s = Math.min(1, Math.max(0.5, cardScale));
  const gridSm = s >= 1 ? 12 : 6;
  const gridMd = s >= 1 ? 6 : s >= 0.75 ? 4 : 3;
  const gridLg = s >= 1 ? 4 : s >= 0.75 ? 3 : 2;
  const minHx = 240 * s;
  const minHm = 260 * s;
  const contentPad = 3 * s;
  const mbIcon = 2 * s;
  const mbTitle = 1.5 * s;
  const descRem = 0.9 + (1.05 - 0.9) * Math.min(1, Math.max(0, (s - 0.5) / 0.5));

  const modulo = moduloPath
    ? allModuleMenus().find((item) => item.path === moduloPath)
    : undefined;

  const subItemsVisibles = useMemo(() => {
    let items = modulo?.subItems ?? [];
    if (moduloPath === '/contabilidad') {
      items = items.filter((item) => !CONTABILIDAD_ACCESOS_OCULTOS.has(item.path));
    }
    if (moduloPath === '/inventario' && canPage(fashionMenuItem.path)) {
      items = [
        ...items,
        {
          text: fashionMenuItem.text,
          icon: fashionMenuItem.icon,
          path: fashionMenuItem.path,
          subItems: fashionMenuItem.subItems,
        },
      ];
    }
    return items.filter((item) => canPage(item.path));
  }, [modulo, moduloPath, canPage]);

  const items: ModuloFuncionalidadItem[] = useMemo(
    () => customItems
      ?? subItemsVisibles.map((item) => ({
        text: item.text,
        path: item.path,
        icon: item.icon,
        description: item.subItems?.length
          ? item.subItems.map((sub) => sub.text).join(' · ')
          : `Accedé a ${item.text.toLowerCase()} de ${modulo?.text.toLowerCase() ?? 'este módulo'}.`,
      })),
    [customItems, subItemsVisibles, modulo],
  );

  const itemsKey = items.map((item) => item.path).join('|');

  useEffect(() => {
    setSearchTerm('');
  }, [itemsKey]);

  const filtrados = useMemo(() => {
    const conIndice = items.map((item, index) => ({ item, index }));
    const q = normalizar(searchTerm.trim());
    if (!q) return conIndice;
    return conIndice.filter(({ item }) => (
      normalizar(item.text).includes(q) || normalizar(item.description ?? '').includes(q)
    ));
  }, [items, searchTerm]);

  const cambiarVista = (next: VistaMenu) => {
    setVista(next);
    try {
      localStorage.setItem(VISTA_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const abrir = (path: string) => {
    if (adminBackTo) {
      navigateWithAdminBack(navigate, path, adminBackTo);
      return;
    }
    clearAdminBackTo();
    if (moduloPath && matchesSharedModuleRoute(path)) {
      navigateWithModuleBack(navigate, path, moduloPath);
      return;
    }
    navigate(path);
  };

  if (!customItems && (!modulo || !subItemsVisibles.length)) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography color="text.secondary">
          No hay funcionalidades configuradas para este módulo.
        </Typography>
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography color="text.secondary">
          No hay funcionalidades configuradas para este módulo.
        </Typography>
      </Box>
    );
  }

  const renderTarjetas = () => (
    <Grid container spacing={2.5}>
      {filtrados.map(({ item, index }) => (
        <Grid item xs={12} sm={gridSm} md={gridMd} lg={gridLg} key={item.path}>
          <Card sx={{ ...cardSx, minHeight: { xs: minHx, sm: minHm } }}>
            <CardActionArea onClick={() => abrir(item.path)} sx={{ height: '100%' }}>
              <CardContent sx={{ p: contentPad }}>
                <Box sx={{ mb: mbIcon }}>
                  <CajaIcono color={COLORES_ICONOS_SUBMENU[index % COLORES_ICONOS_SUBMENU.length]} size={60}>
                    {item.icon}
                  </CajaIcono>
                </Box>
                <Typography
                  variant={s < 0.72 ? 'h6' : 'h5'}
                  sx={{ fontWeight: 700, color: '#111827', mb: mbTitle }}
                >
                  {item.text}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: `${descRem}rem` }}>
                  {item.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderIconos = () => (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
          lg: 'repeat(6, minmax(0, 1fr))',
        },
      }}
    >
      {filtrados.map(({ item, index }) => (
        <Card key={item.path} sx={{ ...cardSx, minHeight: 132 }}>
          <CardActionArea onClick={() => abrir(item.path)} sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2, px: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CajaIcono color={COLORES_ICONOS_SUBMENU[index % COLORES_ICONOS_SUBMENU.length]} size={48}>
                  {item.icon}
                </CajaIcono>
              </Box>
              <Typography
                sx={{
                  mt: 1.25,
                  fontWeight: 700,
                  color: '#111827',
                  fontSize: '0.875rem',
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {item.text}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Box>
  );

  const renderLista = () => (
    <Grid container spacing={1.5}>
      {filtrados.map(({ item, index }) => (
        <Grid item xs={12} md={6} key={item.path}>
          <Card sx={cardSx}>
            <CardActionArea onClick={() => abrir(item.path)}>
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 1.5,
                  px: 2,
                  '&:last-child': { pb: 1.5 },
                }}
              >
                <CajaIcono color={COLORES_ICONOS_SUBMENU[index % COLORES_ICONOS_SUBMENU.length]} size={44}>
                  {item.icon}
                </CajaIcono>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: '#111827' }} noWrap>
                    {item.text}
                  </Typography>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }} noWrap>
                    {item.description}
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        pt: showBackButton ? 0 : { xs: 2, md: 3 },
        pb: { xs: 2, md: 3 },
        bgcolor: '#f3f4f6',
        minHeight: '100vh',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        {showBackButton && (
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', alignSelf: 'flex-start', flexShrink: 0 }}
          >
            {backLabel}
          </Button>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            inputProps={{ 'aria-label': 'Buscar funcionalidad' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#1976d2' }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' } }}
          />
          <Box
            role="group"
            aria-label="Cambiar vista del menú"
            sx={{
              display: 'flex',
              gap: 0.5,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              bgcolor: '#fff',
              p: 0.5,
              alignSelf: { xs: 'flex-end', sm: 'center' },
              flexShrink: 0,
            }}
          >
            <Tooltip title="Vista lista">
              <IconButton
                size="small"
                onClick={() => cambiarVista('lista')}
                sx={vistaButtonSx(vista === 'lista')}
                aria-label="Vista lista"
                aria-pressed={vista === 'lista'}
              >
                <ViewListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vista tarjetas">
              <IconButton
                size="small"
                onClick={() => cambiarVista('tarjetas')}
                sx={vistaButtonSx(vista === 'tarjetas')}
                aria-label="Vista tarjetas"
                aria-pressed={vista === 'tarjetas'}
              >
                <ViewAgendaIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vista iconos">
              <IconButton
                size="small"
                onClick={() => cambiarVista('iconos')}
                sx={vistaButtonSx(vista === 'iconos')}
                aria-label="Vista iconos"
                aria-pressed={vista === 'iconos'}
              >
                <ViewModuleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Stack>

      {filtrados.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No hay funcionalidades que coincidan con la búsqueda.
        </Typography>
      ) : vista === 'iconos' ? (
        renderIconos()
      ) : vista === 'lista' ? (
        renderLista()
      ) : (
        renderTarjetas()
      )}
    </Box>
  );
};

export default ModuloFuncionalidades;
